import React, { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { DistanceField, KeywordField, LocationField } from '../fields'
import { Button } from '../../buttons'
import { ClearButton, SubmitButton } from '../buttons'
import { TextareaAutosize } from '@mui/material'
import { useI18n } from '@ggs/i18n'
import { composeFormValueQueryString, getDrupalSetting } from '@ggs/utils'
import './SearchWidgetGenerator.scss'

/**
 * Search widget generator form.
 * @return {JSX.Element}
 */
const SearchWidgetGenerator = () => {
  /**
   * @type {React.MutableRefObject<HTMLTextAreaElement>}
   */
  const codeTextAreaRef = useRef(null)
  const defaultDistance = '50'
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors: formErrors },
    reset,
    register,
    resetField,
    setError,
    clearErrors,
  } = useForm({})

  const { t } = useI18n()
  const [codeSnippet, setCodeSnippet] = useState('')

  /**
   * Reset the widget generator form.
   */
  const onReset = () => {
    reset()
    setCodeSnippet('')
  }

  /**
   * Generate search widget embeddable code.
   * @param {Object<string, any>} formValues
   * @return {string}
   */
  const generateCodeSnippet = (formValues) => {
    const queryString = composeFormValueQueryString(formValues) || ''
    const baseUrl = getDrupalSetting('baseUrl') || `${process.env.REACT_APP_DRUPAL_BASE_URL}`
    const sourceUrl = `${baseUrl}/search-widget` + '?' + queryString
    const iframeStyle = 'width:100%;height:686px;border:0;'
    return `<iframe src="${sourceUrl}" style="${iframeStyle}" title="211 Search Form Widget"></iframe>`
  }

  /**
   * Form submit handler.
   * @param {Object<string, any>} formValues
   */
  const onSubmit = (formValues) => {
    setCodeSnippet(generateCodeSnippet(formValues))
  }

  /**
   * On code snippet click handler.
   * @param {React.MouseEvent<HTMLTextAreaElement>} event
   */
  const onCodeSnippetClick = async (event) => {
    const targetElement = codeTextAreaRef.current
    // Select all text.
    targetElement.select()
    await navigator.clipboard.writeText(codeSnippet)
  }

  return (
    <div className="search-widget-generator">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="search-widget-generator__form-fields">
          <div className="search-widget-generator__form-filter-item">
            <KeywordField control={control} errors={formErrors} />
          </div>
          <div className="search-widget-generator__form-filter-item">
            <LocationField
              register={register}
              errors={formErrors}
              setValueCallback={setValue}
              getValues={getValues}
              resetValueCallback={resetField}
              errorCallback={setError}
              clearErrorCallback={clearErrors}
            />
          </div>
          {/*<div className="search-widget-generator__form-filter-item">*/}
          {/*  <ConditionField control={control} errors={formErrors} />*/}
          {/*</div>*/}
          {/*<div className="search-widget-generator__form-filter-item">*/}
          {/*  <ProgramServiceField control={control} errors={formErrors} />*/}
          {/*</div>*/}
          {/*<div className="search-widget-generator__form-filter-item">*/}
          {/*  <LanguageField control={control} errors={formErrors} />*/}
          {/*</div>*/}
          <div className="search-widget-generator__form-filter-item-full">
            <DistanceField control={control} errors={formErrors} defaultValue={defaultDistance} />
          </div>
        </div>
        <div className="search-widget-generator__form-actions">
          <SubmitButton isDataLoading={false} errors={formErrors} label={t('Generate Code')} />
          <ClearButton
            isDataLoading={false}
            errors={formErrors}
            label={t('Clear Options')}
            onReset={onReset}
          />
        </div>
      </form>
      <div className="search-widget-generator__form-code">
        {codeSnippet && (
          <>
            <TextareaAutosize
              ref={codeTextAreaRef}
              onClick={onCodeSnippetClick}
              minRows={10}
              maxRows={15}
              value={codeSnippet}
              style={{ width: '100%' }}
              readOnly={true}
            />
            <div className="search-widget-generator__form-code-actions">
              <Button
                label={t('Copy Source Code')}
                variant="text"
                color="secondary"
                onClick={async () => {
                  await navigator.clipboard.writeText(codeSnippet)
                }}
              />
              <Button
                label={t('Have Questions? Contact Us For Help')}
                variant="text"
                color="secondary"
                link="/contact-us"
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default SearchWidgetGenerator
